<template>
    <div class="flex w-full flex-col gap-4">
        <div class="flex w-full gap-4">
            <div class="grid w-full auto-rows-min gap-2">
                <button v-if="currentProduct?.extras && Object.keys(currentProduct.extras).length" class="min-w-0 appearance-none break-words text-left text-base underline hover:opacity-70" @click.prevent="productForm().editProductFromCart(cartProduct, cartProductIndex)">
                    {{ capitalize(currentProduct.title) }}
                </button>
                <span v-else class="min-w-0 break-words text-left text-base">
                    {{ capitalize(currentProduct.title) }}
                </span>
                <div v-if="cartProduct?.extras && Object.keys(cartProduct.extras).length" class="min-w-0 break-words text-xs text-gray-400">
                    {{ currentProductExtrasLabels }}
                </div>
                <template v-if="webshopSettings().isRemarksTurnedOn">
                    <button
                        v-if="cartProduct.note"
                        type="button"
                        class="flex w-full gap-2 rounded-md bg-amber-50 p-3 text-left text-xs transition-opacity hover:opacity-70"
                        @click="openNoteModal()"
                    >
                        <Icon :icon="IconPen" :auto-align="false" />
                        <span>{{ cartProduct.note }}</span>
                    </button>
                    <button
                        v-if="!cartProduct.note || cartProduct.note === ''"
                        type="button"
                        class="flex cursor-pointer items-center text-xs underline"
                        @click="openNoteModal()"
                    >
                        <Icon :icon="IconPen" class="mr-1" :auto-align="false" />
                        {{ $t('checkoutPage.payment.ADDremarks') }}
                    </button>
                </template>
            </div>
            <div class="grid justify-items-end gap-3">
                <div class="flex flex-col gap-1 text-right">
                    <div class="flex justify-end gap-2">
                        <ProductOriginalPrice v-if="currentProduct?.originalPrice" :original-price="currentProduct.originalPrice" />
                        <div class="flex shrink-0 flex-col">
                            <template v-if="cartDebugMode">
                                <span
                                    v-if="cartProduct?.priceLocal"
                                    class="text-base font-bold tabular-nums"
                                    :class="cartProduct.priceLocal !== cartProduct.priceVerified ? 'text-red-500' : 'text-green-500'"
                                >
                                    CL: {{ currencyFormat(cartProduct.priceLocal) }}
                                </span>
                                <span
                                    v-if="cartProduct?.priceVerified"
                                    class="text-base font-bold tabular-nums"
                                    :class="cartProduct.priceLocal !== cartProduct.priceVerified ? 'text-red-500' : 'text-green-500'"
                                >
                                    CS:{{ currencyFormat(cartProduct.priceVerified) }}
                                </span>
                            </template>
                            <span
                                v-else
                                class="text-base font-bold tabular-nums"
                            >
                                {{ predictPriceOfProduct(currentProduct, cartProduct) }}
                            </span>
                        </div>
                    </div>
                    <span v-if="currentProduct?.deposit > 0 && false" class="flex appearance-none justify-end gap-2 text-xs">
                        <Icon :icon="IconDeposit" class="fill-black text-sm" :auto-align="false" />
                        <span>
                            {{ $t('cart.costs.deposit') }}: {{ currencyFormat(currentProduct.deposit * cart().cart.products[cartProductIndex].quantity) }}
                        </span>
                    </span>
                    <span v-if="currentProduct?.packagingCosts > 0 && false" class="flex appearance-none justify-end gap-2 text-xs">
                        <Icon :icon="IconPackaging" class="fill-black text-sm" :auto-align="false" />
                        <span>
                            {{ $t('cart.costs.packaging') }}: {{ currencyFormat(currentProduct.packagingCosts * cart().cart.products[cartProductIndex].quantity) }}
                        </span>
                    </span>
                </div>
                <span class="justify-end">
                    <FormKit
                        :id="`${cartProductIndex}-product`"
                        v-model="cart().cart.products[cartProductIndex]"
                        type="group"
                        :name="cartProductIndex"
                        validation="+handleMinimumOrderQuantity"
                        :validation-rules="{ handleMinimumOrderQuantity }"
                        validation-visibility="live"
                        :validation-messages="{ handleMinimumOrderQuantity: t('cart.limitations.productMinimumNotReached', { min: currentProduct?.minimumOrderQuantity }) }"
                    >
                        <FormKit
                            :id="`${cartProductIndex}-quantity`"
                            :key="`${cartProductIndex}-quantity-${locale}`"
                            v-model="cart().cart.products[cartProductIndex].quantity"
                            name="quantity"
                            type="stepNumber"
                            :small="true"
                            :max-message="t('cart.limitations.maxReached')"
                            :deletable="true"
                            :min="1"
                            :max="getProductMaximumOrderQuantity(currentProduct, cart().cart.products[cartProductIndex].quantity)" />
                    </FormKit>
                </span>
            </div>
        </div>
        <transition name="fade">
            <FormKitMessages
                :node="getNode(`${cartProductIndex}-product`) ?? undefined"
                :library="FormKitMessagesCustomComponents"
                :sections-schema="
                    // @ts-expect-error
                    {
                        message: {
                            $el: undefined,
                            $cmp: 'Notification',
                            props: {
                                type: 'error'
                            },
                            children: '$message.value'
                        }
                    }"
            />
        </transition>
    </div>
</template>

<script setup lang="ts">
    import type { FormKitNode } from '@formkit/core'
    import type { CartProductCard } from '~/types/local-types'
    import { getNode } from '@formkit/core'
    import { FormKitMessages } from '@formkit/vue'
    import capitalize from 'lodash.capitalize'
    import { useModalSlot } from 'vue-final-modal'
    import IconDeposit from '@/assets/icons/deposit.svg'
    import IconPen from '@/assets/icons/pen.svg'
    import IconPackaging from '~/assets/icons/packaging.svg'
    import Icon from '~/components/Icon.vue'
    import ModalContentProductNote from '~/components/Modal/Content/ModalContentProductNote.vue'
    import Notification from '~/components/Notification/Notification.vue'
    import ProductOriginalPrice from '~/components/Product/ProductOriginalPrice.vue'
    import { messagesModal } from '~/composables/messagesModal'
    import { getProductMaximumOrderQuantity } from '~/composables/productLimitations'
    import { cart } from '~/store/cart'
    import { productForm } from '~/store/productForm'
    import { shop } from '~/store/shop'
    import { webshopSettings } from '~/store/webshopSettings'
    const props = defineProps<CartProductCard>()
    const FormKitMessagesCustomComponents = { Notification: markRaw(Notification) }
    const { t, locale } = useI18n()
    const { public: { cartDebugMode } } = useRuntimeConfig()
    const showRemarks = ref(false)
    const currentProduct = shop().findProduct(props.cartProduct.id)
    const currentProductExtrasLabels = computed(() => {
        const arr: string[] = []
        let extraDisplayLabels: string = ''
        if (props.cartProduct?.extras && Object.keys(props.cartProduct.extras).length) {
            Object.keys(props.cartProduct?.extras).forEach(extraKey => {
                Object.keys(props.cartProduct?.extras[extraKey].items).forEach(extraItemKey => {
                    if (props.cartProduct?.extras[extraKey].items[extraItemKey].quantity > 0) {
                        if (shop().findExtraItem(extraItemKey)?.title && shop().findExtraItem(extraItemKey)?.title !== '') {
                            arr.push(`${shop().findExtraItem(extraItemKey)?.title}${props.cartProduct?.extras[extraKey].items[extraItemKey].quantity > 1 ? (props.cartProduct?.extras[extraKey].items[extraItemKey].quantity) : ''}`)
                        }
                    }
                })
            })
        }
        extraDisplayLabels = extraDisplayLabels + arr.join(', ')
        return extraDisplayLabels
    })
    function handleMinimumOrderQuantity (node: FormKitNode) {
        const parent = node.at('$parent')
        // TODO: consider partially replacing the below code with getCartQuantityForProduct. but we will need to add a "cart" prop to getCartQuantityForProduct, because the validation wont run if we do not refer it to its parent node
        return currentProduct?.minimumOrderQuantity > 1 && parent?.value && Object.values(parent.value).some((product: any) => product.id === node.value.id)
            ? Object.values(parent.value).filter((product: any) => product.id === node.value.id).reduce((acc: number, product: any) => acc + product.quantity, 0) >= currentProduct?.minimumOrderQuantity
            : true
    }
    async function openNoteModal () {
        const modalInstance = await messagesModal({
            default: useModalSlot({
                component: ModalContentProductNote,
                attrs: {
                    cartProductIndex: props.cartProductIndex,
                    closeModal: () => {
                        modalInstance.close()
                    }
                }
            })
        })
        showRemarks.value = false
    }
    onMounted(() => {
        const currentNode = getNode(`${props.cartProductIndex}-quantity`)
        if (currentNode) {
            currentNode.on('delete', () => {
                cart().deleteProduct(props.cartProduct, props.cartProductIndex)
            })
        }
    })
</script>
